import React, { useState, useCallback, useEffect } from "react";
import { validarClaveAccesoCamara } from "../../API/accesoCamara";
import VideoReproductor from "./VideoReproductor";
import "./AccesoCamara.css";

const MENSAJE_CARGA = "Validando código y obteniendo transmisión…";
const MENSAJE_SESION_CERRADA =
  "Se cumplió el tiempo máximo de acceso (59 minutos desde el inicio de la cita). La transmisión se cerró.";

function AccesoCamara() {
  const [codigoAcceso, setCodigoAcceso] = useState("");
  const [error, setError] = useState("");
  const [infoSesion, setInfoSesion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [stream, setStream] = useState(null);

  const reiniciarTrasCambioCodigo = useCallback(() => {
    setStream(null);
    setError("");
    setInfoSesion(null);
  }, []);

  const manejarVerTransmision = useCallback(async () => {
    setError("");
    setStream(null);
    setInfoSesion(null);
    setCargando(true);
    try {
      const r = await validarClaveAccesoCamara(codigoAcceso);
      if (r.ok && r.data) {
        setStream({
          playbackUrl: r.data.playbackUrl,
          playerType: r.data.playerType || "hls",
          expiresAt: r.data.expiresAt,
        });
        setInfoSesion({
          codigoCubiculo: r.data.codigoCubiculo || "",
          nombreConsultorio: r.data.nombreConsultorio || "",
          mensaje: r.data.mensaje || "Acceso autorizado.",
          modoPruebaHorario: !!r.data.modoPruebaHorario,
          horaServidor: r.data.horaServidor || "",
          horaCita: r.data.horaCita || "",
        });
      } else {
        setError(r.message || "Acceso no autorizado.");
      }
    } catch (err) {
      const msg =
        err.response && err.response.data && err.response.data.variable2
          ? String(err.response.data.variable2)
          : err.message || "Error de red al contactar el servidor.";
      setError(msg);
    } finally {
      setCargando(false);
    }
  }, [codigoAcceso]);

  const manejarErrorVideo = useCallback(() => {
    setError(
      "No se pudo reproducir el video del consultorio. Compruebe su conexión e intente de nuevo con el mismo código. Si el problema continúa, la cámara o el servidor de transmisión pueden estar fuera de línea."
    );
    setStream(null);
  }, []);

  // Cierra cuando el tiempo es mayor a hora_inicio + 59 min (expiresAt inclusive).
  useEffect(() => {
    if (!stream || !stream.expiresAt) {
      return undefined;
    }
    const expiresMs = Number(stream.expiresAt) * 1000;
    if (!expiresMs || Number.isNaN(expiresMs)) {
      return undefined;
    }
    const restante = expiresMs - Date.now();
    // Aún válido en el segundo exacto de expiresAt; cierra al pasar de los 59 min.
    if (restante < 0) {
      setStream(null);
      setError(MENSAJE_SESION_CERRADA);
      return undefined;
    }
    const timer = setTimeout(() => {
      setStream(null);
      setError(MENSAJE_SESION_CERRADA);
    }, restante + 1);
    return () => clearTimeout(timer);
  }, [stream]);

  const codigoListo = String(codigoAcceso).trim().replace(/\s+/g, "").length >= 4;

  return (
    <div className="acceso-camara">
      <section className="acceso-camara-panel">
        <h2>Acceso a la terapia en cámara</h2>
        <p className="acceso-camara-ayuda">
          Ingrese el <strong>código de acceso</strong> que recibió por correo al
          confirmar la cita. Durante los{" "}
          <strong>59 minutos desde la hora de inicio</strong> de la terapia,
          pulse <strong>Ver transmisión</strong> para abrir el video del
          consultorio. Ejemplo: cita a las 11:00 → acceso hasta las 11:59; al
          pasar de los 59 minutos se cierra.
        </p>
        <div className="acceso-camara-form">
          <label className="acceso-camara-field">
            <span>Código de acceso</span>
            <input
              type="text"
              name="codigoAcceso"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              value={codigoAcceso}
              placeholder="Ej. el código del correo"
              onChange={(ev) => {
                setCodigoAcceso(ev.target.value);
                reiniciarTrasCambioCodigo();
              }}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") {
                  ev.preventDefault();
                  if (codigoListo && !cargando) void manejarVerTransmision();
                }
              }}
            />
          </label>
          <div className="acceso-camara-actions">
            <button
              type="button"
              className="acceso-camara-submit acceso-camara-submit--primero"
              disabled={cargando || !codigoListo}
              title={
                !codigoListo
                  ? "Escriba el código que recibió por correo"
                  : undefined
              }
              onClick={() => void manejarVerTransmision()}
            >
              {cargando ? MENSAJE_CARGA : "Ver transmisión"}
            </button>
          </div>
        </div>
        {!codigoListo ? (
          <p className="acceso-camara-hint" aria-live="polite">
            Escriba el código del correo para habilitar «Ver transmisión».
          </p>
        ) : null}
        {infoSesion && (infoSesion.codigoCubiculo || infoSesion.nombreConsultorio || infoSesion.modoPruebaHorario) ? (
          <div className="acceso-camara-conexion" aria-label="Detalle de sesión">
            <h3 className="acceso-camara-conexion-titulo">Sesión</h3>
            <dl className="acceso-camara-conexion-dl">
              {infoSesion.nombreConsultorio ? (
                <>
                  <dt>Consultorio</dt>
                  <dd>{infoSesion.nombreConsultorio}</dd>
                </>
              ) : null}
              {infoSesion.codigoCubiculo ? (
                <>
                  <dt>Código</dt>
                  <dd>{infoSesion.codigoCubiculo}</dd>
                </>
              ) : null}
              {infoSesion.horaCita ? (
                <>
                  <dt>Hora cita</dt>
                  <dd>{infoSesion.horaCita}</dd>
                </>
              ) : null}
              {infoSesion.horaServidor ? (
                <>
                  <dt>Hora servidor</dt>
                  <dd>{infoSesion.horaServidor}</dd>
                </>
              ) : null}
              {infoSesion.modoPruebaHorario ? (
                <>
                  <dt>Aviso</dt>
                  <dd>Modo prueba: el servidor tiene fecha/hora forzada</dd>
                </>
              ) : null}
            </dl>
          </div>
        ) : null}
        {error ? (
          <div className="acceso-camara-error" role="alert">
            {error}
          </div>
        ) : null}
      </section>

      {stream && stream.playbackUrl ? (
        <section className="acceso-camara-video-wrap">
          <h3>Transmisión</h3>
          <div className="acceso-camara-video-box">
            <VideoReproductor
              playbackUrl={stream.playbackUrl}
              playerType={stream.playerType}
              onError={manejarErrorVideo}
            />
          </div>
          {stream.expiresAt ? (
            <p className="acceso-camara-expira">
              La transmisión se cierra automáticamente al cumplirse 59 minutos
              desde la hora de inicio de la cita.
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default AccesoCamara;
